import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, matchPath } from "react-router-dom";
import { Toaster } from "sonner";
import { Helmet } from "react-helmet-async";

import Home from "@/pages/Home";
import Analysis from "@/pages/Analysis";
import History from "@/pages/History";
import LegalPage from "@/pages/LegalPage";
import AdminPage from "@/pages/AdminPage";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import CookieBanner from "@/components/CookieBanner";
import InAppBrowserBanner from "@/components/InAppBrowserBanner";
import PWAInstallButton from "@/components/PWAInstallButton";
import WaitlistBar from "@/components/WaitlistBar";
import LegalFooter from "@/components/LegalFooter";
import LegalConsentBanner from "@/components/LegalConsentBanner";
import AuthCallback from "@/components/AuthCallback";
import OnboardingHost from "@/components/OnboardingHost";
import { AuthProvider } from "@/context/AuthContext";

/**
 * App-level default SEO. These meta tags are the single source of truth for
 * OpenGraph / Twitter / description / canonical across the whole SPA — the
 * static duplicates were removed from public/index.html so that Helmet on
 * BlogPost.jsx can OVERRIDE them per-article without duplication.
 *
 * IMPORTANT: react-helmet-async v3 does NOT reliably dedupe between two
 * concurrently-mounted <Helmet> components. If BlogPost's per-article Helmet
 * is mounted at the same time as this default one, the head ends up with
 * two entries per og / twitter / canonical tag and social crawlers grab the
 * first one (the default) instead of the article-specific one. So we skip
 * DefaultSEO entirely on the article detail route.
 */
function DefaultSEO() {
  const { pathname } = useLocation();
  if (matchPath("/blog/:slug", pathname)) return null;
  if (pathname === "/blog") return null;
  return (
    <Helmet>
      <meta
        name="description"
        content="Spectraloom è il tuo fonico AI sempre in studio. Carica il tuo mix, scegli il genere e ricevi analisi DSP completa, mastering automatico professionale e ottimizzazione EQ, compressione e limiter pronta al download."
      />
      <link rel="canonical" href={`https://spectraloom.app${pathname === "/" ? "/" : pathname}`} />
      {/* hreflang — only emitted on the home route so /blog and /blog/{slug}
          keep their own page-specific alternates without conflict. */}
      {pathname === "/" && (
        <>
          <link rel="alternate" hrefLang="it" href="https://spectraloom.app/?lang=it" />
          <link rel="alternate" hrefLang="en" href="https://spectraloom.app/?lang=en" />
          <link rel="alternate" hrefLang="x-default" href="https://spectraloom.app/" />
        </>
      )}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Spectraloom" />
      <meta
        property="og:title"
        content="Spectraloom — AI Mix & Master Analyzer | Online Mastering Tool"
      />
      <meta
        property="og:description"
        content="AI-powered mix analysis, DSP metrics and automatic mastering. Pro feedback, LUFS optimization and 48kHz/24-bit WAV export — your studio in the cloud."
      />
      <meta property="og:url" content="https://spectraloom.app/" />
      <meta property="og:image" content="https://spectraloom.app/og-image.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="Spectraloom — AI Mix and Master Analyzer dashboard preview"
      />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="it_IT" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@spectraloom" />
      <meta
        name="twitter:title"
        content="Spectraloom — AI Mix & Master Analyzer"
      />
      <meta
        name="twitter:description"
        content="Your mastering engineer, always in studio. AI mix analysis + automatic mastering — free tier available."
      />
      <meta name="twitter:image" content="https://spectraloom.app/og-image.png" />
      <meta
        name="twitter:image:alt"
        content="Spectraloom AI mastering dashboard preview"
      />
    </Helmet>
  );
}

/**
 * WorkOS AuthKit returns to `/auth/callback?code=...` — the route below
 * mounts <AuthCallback /> which exchanges the code for a session token
 * via POST /api/auth/callback.
 */
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/analysis/:id" element={<Analysis />} />
      <Route path="/history" element={<History />} />
      <Route path="/legal/:doc" element={<LegalPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <DefaultSEO />
        <AuthProvider>
          <WaitlistBar />
          <InAppBrowserBanner />
          <AppRouter />
          <LegalFooter />
          <CookieBanner />
          <PWAInstallButton />
          <OnboardingHost />
          <LegalConsentBanner />
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#1a1a1a",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "white",
          },
        }}
      />
    </div>
  );
}

export default App;
